import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  CalendarToday as CalendarIcon,
  Close,
} from '@mui/icons-material';
import { Event, EventFilterParams, TableColumn } from '../types';
import apiService from '../services/api';
import EventViewDialog from '../components/modals/EventViewDialog';
import EventEditDialog from '../components/modals/EventEditDialog';
import DataTable from '../components/dataTable';

const Events: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [filters, setFilters] = useState<EventFilterParams>({
    page: 1,
    limit: 25,
    searchTerm: '',
    isActive: undefined,
  });

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [viewingEvent, setViewingEvent] = useState<Event | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const eventsResponse = await apiService.getEvents(filters);
      setEvents(eventsResponse.data || []);
      setTotal(eventsResponse.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [filters]);

  const handleViewEvent = (event: Event) => {
    setViewingEvent(event);
    setViewDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setEditDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const handleDeleteEvent = (event: Event) => {
    setEventToDelete(event);
    setDeleteDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const confirmDelete = async () => {
    if (!eventToDelete) return;

    try {
      await apiService.deleteEvent(eventToDelete.id);
      setDeleteDialogOpen(false);
      setEventToDelete(null);
      fetchEvents();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete event');
    }
  };

  const handleToggleStatus = async (event: Event) => {
    try {
      await apiService.toggleEventStatus(event.id);
      fetchEvents();
      setActionMenuAnchor(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to toggle event status');
    }
  };

  const handleEventSaved = () => {
    setEditDialogOpen(false);
    setEditingEvent(null);
    fetchEvents();
  };

  const handleFilterChange = (field: keyof EventFilterParams, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
    setPage(0);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setFilters(prev => ({ ...prev, limit: newRowsPerPage, page: 1 }));
  };

  const openActionMenu = (
    event: React.MouseEvent<HTMLElement>,
    eventItem: Event
  ) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedEvent(eventItem);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedEvent(null);
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? { bg: '#dcfce7', color: '#16a34a' }
      : { bg: '#fee2e2', color: '#dc2626' };
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // if (loading && events.length === 0) {
  //   return (
  //     <Box
  //       sx={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         height: '50vh',
  //       }}
  //     >
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  const columns: TableColumn<Event>[] = [
    { label: 'Agenda Name', key: 'eventName' },
    { label: 'Agenda Code', key: 'eventCode' },
    {
      label: 'Date Range',
      keys: ['startDate', 'endDate'],
      renderGroup: values => {
        const [start, end] = values as [string | Date, string | Date];

        const format = (d: string | Date) =>
          new Date(d).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '0.875rem' }}>{format(start)}</span>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              to {format(end)}
            </span>
          </Box>
        );
      },
    },
    { label: 'Venue', key: 'venue' },
    { label: 'Status', key: 'isActive', chip: 'status' },
    { label: 'Registrations', key: 'registrations' },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.875rem',
            fontWeight: 700,
            color: '#111827',
          }}
        >
          Agenda Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            bgcolor: '#3b82f6',
            '&:hover': { bgcolor: '#2563eb' },
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            py: 1,
          }}
          onClick={() => setEditDialogOpen(true)}
        >
          Create Agenda
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Main Content Card */}
      <Card
        sx={{
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
        }}
      >
        {/* Filters Section */}
        <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flex: 1,
                flexWrap: 'wrap',
              }}
            >
              <TextField
                placeholder="Search agenda..."
                value={filters.searchTerm}
                onChange={e => handleFilterChange('searchTerm', e.target.value)}
                size="small"
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={
                    filters.isActive === undefined
                      ? ''
                      : filters.isActive.toString()
                  }
                  onChange={e =>
                    handleFilterChange(
                      'isActive',
                      e.target.value === ''
                        ? undefined
                        : e.target.value === 'true'
                    )
                  }
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Start Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.startDate || ''}
                onChange={e =>
                  handleFilterChange(
                    'startDate',
                    e.target.value ? e.target.value : undefined
                  )
                }
              />

              <TextField
                label="End Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.endDate || ''}
                onChange={e =>
                  handleFilterChange(
                    'endDate',
                    e.target.value ? e.target.value : undefined
                  )
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Table */}
        <DataTable<Event>
          columns={columns}
          data={events}
          actions={row => [
            {
              icon: <VisibilityIcon fontSize="small" />,
              onClick: () => handleViewEvent(row),
            },
            {
              icon: <EditIcon fontSize="small" />,
              onClick: () => handleEditEvent(row),
            },
            {
              icon: <MoreVertIcon fontSize="small" />,
              onClick: e => openActionMenu(e, row),
            },
          ]}
        />

        {/* Pagination */}
        {events.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-toolbar': {
                px: 3,
              },
            }}
          />
        )}
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeActionMenu}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow:
              '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            mt: 1,
            minWidth: 150,
          },
        }}
      >
        <MenuItem
          onClick={() => selectedEvent && handleToggleStatus(selectedEvent)}
        >
          <ListItemIcon>
            {selectedEvent?.isActive ? (
              <ToggleOffIcon fontSize="small" />
            ) : (
              <ToggleOnIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedEvent?.isActive ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => selectedEvent && handleDeleteEvent(selectedEvent)}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* View Dialog */}
      <EventViewDialog
        open={viewDialogOpen}
        event={viewingEvent}
        onClose={() => {
          setViewDialogOpen(false);
          setViewingEvent(null);
        }}
      />

      {/* Edit Dialog */}
      <EventEditDialog
        open={editDialogOpen}
        event={editingEvent}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingEvent(null);
        }}
        onSave={handleEventSaved}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              {' '}
              Confirm Delete{' '}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete event "{eventToDelete?.eventName}"?
            This action cannot be undone and will also remove all associated
            registrations and sessions.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Events;
